import React, { useState, useEffect } from 'react'
import { Field, ErrorMessage, useFormikContext } from 'formik'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  TrashIcon,
  PlayIcon,
  CheckCircleIcon,
  PauseIcon,
  ImageIcon,
  XIcon,
  FileTextIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import { EventAudienceField } from './EventAudienceField'
import { GameRoomDescriptionField } from './GameRoomDescription'
import ConfirmationModal from '../OGE/OGEDeletionModal'
import { EventMediaGeneratorChat } from './EventMediaGeneratorChat'
import {
  deleteEventMediaAPI,
  generateTTQDocumentAPI,
  fetchQuillTypesAPI,
  TTQApiError,
  type QuillType,
} from '@/api/room'
import { MAX_EVENT_MEDIA_PER_EVENT } from '@/constants/eventMedia'
import { EventMediaItem, GameroomEvent } from '@/redux/types/room'

interface EventCardProps {
  event: GameroomEvent
  index: number
  isGameCompleted: boolean
  isGameInProgress: boolean
  onDelete: (eventId: number) => void
  onRelease: (eventId: number) => void
  onUnrelease: (eventId: number) => void
  isReleasing?: boolean
  isUnreleasing?: boolean
  roomId?: string
  scenarioTitle?: string
  onMediaAccept?: (index: number, media: EventMediaItem) => void
  isNewEvent?: boolean
}

export const EventCard: React.FC<EventCardProps> = ({
  event,
  index,
  isGameCompleted,
  isGameInProgress,
  onDelete,
  onRelease,
  onUnrelease,
  isReleasing = false,
  isUnreleasing = false,
  roomId,
  scenarioTitle = '',
  onMediaAccept,
  isNewEvent = false,
}) => {
  const [showReleaseDialog, setShowReleaseDialog] = useState<boolean>(false)
  const [showUnreleaseDialog, setShowUnreleaseDialog] = useState<boolean>(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false)
  const [showMediaGenerator, setShowMediaGenerator] = useState<boolean>(false)
  const [previewMediaUrl, setPreviewMediaUrl] = useState<string | null>(null)
  const [mediaToRemoveId, setMediaToRemoveId] = useState<number | null>(null)
  const [isRemovingMedia, setIsRemovingMedia] = useState<boolean>(false)
  const [useTTQ, setUseTTQ] = useState<boolean>(event.use_ttq ?? false)
  const [quillTypes, setQuillTypes] = useState<QuillType[]>([])
  const [selectedQuillType, setSelectedQuillType] = useState<string>('')
  const [isLoadingQuillTypes, setIsLoadingQuillTypes] = useState<boolean>(false)
  const [isGeneratingTTQ, setIsGeneratingTTQ] = useState<boolean>(false)
  const [ttqError, setTtqError] = useState<string | null>(null)

  useEffect(() => {
    if (!useTTQ || !roomId || quillTypes.length > 0) return
    setIsLoadingQuillTypes(true)
    fetchQuillTypesAPI(roomId)
      .then((types) => {
        setQuillTypes(types)
        if (types.length > 0) setSelectedQuillType(types[0].name)
      })
      .catch(() => setTtqError('Failed to load quill types.'))
      .finally(() => setIsLoadingQuillTypes(false))
  }, [useTTQ, roomId])
  const { values, setFieldValue } = useFormikContext<{
    events: GameroomEvent[]
  }>()

  const isReleased = event.is_released
  // Not isReleased: ending a round un-releases events, which would unlock the
  // audience again after players had already seen it.
  const hasEverBeenReleased = event.has_ever_been_released ?? event.is_released
  const isDayZero = index === 0
  const canDelete = !isGameCompleted && index > 0

  const currentEvent = values.events[index]
  const mediaCount = currentEvent?.media?.length ?? 0
  const canAddMoreMedia = mediaCount < MAX_EVENT_MEDIA_PER_EVENT
  const allFieldsFilled =
    currentEvent &&
    currentEvent.title?.trim() &&
    currentEvent.text?.trim() &&
    currentEvent.event_datetime?.trim()

  const canRelease = !isReleased && !isGameCompleted && allFieldsFilled
  const canUnrelease = isReleased && !isGameCompleted

  const handleRelease = () => onRelease(event.id || index)

  const handleUnrelease = () => onUnrelease(event.id || index)

  const handleGenerateTTQ = async () => {
    if (!roomId || !event.id || isNewEvent) {
      toast.error('Please save the scenario before generating a TTQ document.')
      return
    }
    const eventText = currentEvent?.text ?? ''
    if (!eventText.trim()) {
      toast.error('Event text must not be empty.')
      return
    }
    setIsGeneratingTTQ(true)
    setTtqError(null)
    try {
      const result = await generateTTQDocumentAPI(roomId, {
        event_id: event.id,
        quill_type: selectedQuillType,
        markdown_text: eventText,
      })
      const next = [...(currentEvent?.media || []), result]
      setFieldValue(`events[${index}].media`, next)
      setFieldValue(`events[${index}].use_ttq`, false)
      setUseTTQ(false)
      onMediaAccept?.(index, result)
      toast.success('TTQ document generated and attached to event.')
    } catch (err) {
      const msg =
        err instanceof TTQApiError
          ? err.userMessage
          : 'Something went wrong while generating the TTQ document. Please try again.'
      setTtqError(msg)
      toast.error(msg)
    } finally {
      setIsGeneratingTTQ(false)
    }
  }

  return (
    <>
      <Card
        className={`relative transition-all duration-200 hover:shadow-md ${
          isDayZero
            ? 'border-2 border-blue-400 bg-blue-50/30'
            : isReleased
              ? 'border-green-200 bg-green-50/20'
              : 'border-border'
        }`}
      >
        <CardContent className='mt-5 space-y-4'>
          <div className='space-y-2'>
            <div className='flex justify-between items-center'>
              <label className='block text-sm font-medium text-dark-blue'>
                {isDayZero ? (
                  <span className='font-bold'>Day Zero Title</span>
                ) : (
                  'Event Title'
                )}
              </label>

              <div className='flex gap-2 items-center'>
                {roomId && onMediaAccept && !isGameCompleted && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className='inline-block'>
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            onClick={() => setShowMediaGenerator(true)}
                            disabled={!canAddMoreMedia}
                            className='text-dark-blue hover:bg-blue-50 disabled:opacity-50'
                          >
                            <ImageIcon className='w-4 h-4' />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          {canAddMoreMedia
                            ? 'Generate or upload media (image) for this event'
                            : `Maximum ${MAX_EVENT_MEDIA_PER_EVENT} images per event. Remove some to add more.`}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {canDelete && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          onClick={() => setShowDeleteDialog(true)}
                          className='text-red-500 hover:text-red-700 hover:bg-red-50'
                        >
                          <TrashIcon className='w-4 h-4' />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Delete event</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {isReleased ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type='button'
                          size='sm'
                          onClick={() => setShowUnreleaseDialog(true)}
                          disabled={!canUnrelease || isUnreleasing}
                          className={`${
                            canUnrelease
                              ? 'text-white bg-orange-600 hover:bg-orange-700'
                              : 'text-white bg-green-600 cursor-not-allowed'
                          }`}
                        >
                          {isUnreleasing ? (
                            <PauseIcon className='mr-1 w-4 h-4 animate-pulse' />
                          ) : canUnrelease ? (
                            <PauseIcon className='mr-1 w-4 h-4' />
                          ) : (
                            <CheckCircleIcon className='mr-1 w-4 h-4' />
                          )}
                          {canUnrelease ? 'Unrelease' : 'Released'}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          {canUnrelease
                            ? 'Unrelease event from players'
                            : 'Event is released'}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type='button'
                          size='sm'
                          onClick={() => setShowReleaseDialog(true)}
                          disabled={!canRelease || isReleasing}
                          className={`${
                            canRelease
                              ? 'text-white bg-green-600 hover:bg-green-700'
                              : 'text-gray-500 bg-gray-300 cursor-not-allowed'
                          }`}
                        >
                          {isReleasing ? (
                            <PlayIcon className='mr-1 w-4 h-4 animate-pulse' />
                          ) : (
                            <PlayIcon className='mr-1 w-4 h-4' />
                          )}
                          Release
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          {!allFieldsFilled
                            ? 'Fill all fields to release'
                            : 'Release event to players'}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>

            <Field
              as={Input}
              name={`events[${index}].title`}
              placeholder='Enter event title'
              disabled={isGameCompleted || (isReleased && isGameInProgress)}
              className={`${isGameCompleted ? 'bg-muted' : ''}`}
            />
            <ErrorMessage
              name={`events[${index}].title`}
              component='div'
              className='text-sm text-red-500'
            />
          </div>

          <div className='space-y-2'>
            <label className='text-sm font-medium text-foreground'>
              {isDayZero ? (
                <span className='font-bold text-dark-blue'>
                  Scenario Date and Time
                </span>
              ) : (
                <span className='text-dark-blue'>Scenario Date and Time</span>
              )}
            </label>
            <Field
              as={Input}
              type='datetime-local'
              name={`events[${index}].event_datetime`}
              disabled={isGameCompleted || (isReleased && isGameInProgress)}
              className={`${isGameCompleted ? 'bg-muted' : ''}`}
            />
            <ErrorMessage
              name={`events[${index}].event_datetime`}
              component='div'
              className='text-sm text-red-500'
            />
          </div>

          <GameRoomDescriptionField
            name={`events[${index}].text`}
            index={index}
            readOnly={isGameCompleted || (isReleased && isGameInProgress)}
          />

          <EventAudienceField
            index={index}
            disabled={isGameCompleted || hasEverBeenReleased}
          />

          {!isGameCompleted && roomId && (
            <div className='p-3 space-y-2 rounded-md border border-blue-300 border-dashed bg-blue-50/30'>
              <div className='flex gap-2 items-center'>
                <input
                  type='checkbox'
                  id={`use-ttq-${index}`}
                  checked={useTTQ}
                  onChange={(e) => {
                    setUseTTQ(e.target.checked)
                    setFieldValue(`events[${index}].use_ttq`, e.target.checked)
                    setTtqError(null)
                  }}
                  className='w-4 h-4 rounded border-gray-300 cursor-pointer text-dark-blue'
                />
                <label
                  htmlFor={`use-ttq-${index}`}
                  className='text-sm font-medium cursor-pointer select-none text-dark-blue'
                >
                  Use TongueToQuill Format
                </label>
              </div>

              {useTTQ && (
                <div className='flex flex-wrap gap-2 items-center mt-2'>
                  <select
                    value={selectedQuillType}
                    onChange={(e) => setSelectedQuillType(e.target.value)}
                    disabled={isGeneratingTTQ || isLoadingQuillTypes}
                    className='px-2 py-1 text-sm bg-white rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-dark-blue'
                  >
                    {isLoadingQuillTypes ? (
                      <option value=''>Loading…</option>
                    ) : (
                      quillTypes.map((qt) => (
                        <option key={qt.name} value={qt.name}>
                          {qt.name}
                        </option>
                      ))
                    )}
                  </select>

                  <Button
                    type='button'
                    size='sm'
                    onClick={handleGenerateTTQ}
                    disabled={
                      isGeneratingTTQ ||
                      isLoadingQuillTypes ||
                      !selectedQuillType ||
                      !canAddMoreMedia
                    }
                    className='text-xs text-white bg-dark-blue hover:bg-dark-blue-hovered'
                  >
                    {isGeneratingTTQ ? 'Generating…' : 'Generate TTQ Document'}
                  </Button>

                  {ttqError && (
                    <p className='mt-1 w-full text-xs text-red-600'>
                      {ttqError}
                    </p>
                  )}
                  <p className='w-full text-xs text-muted-foreground'>
                    Event text must be written in markdown. The generated
                    document will be stored as event media.
                  </p>
                </div>
              )}
            </div>
          )}

          {currentEvent?.media && currentEvent.media.length > 0 && (
            <div className='flex flex-wrap gap-2 items-center'>
              <span className='text-sm text-muted-foreground'>Media:</span>
              {currentEvent.media.map((m) => (
                <div key={m.id} className='inline-block relative'>
                  {m.type === 'image' && (
                    <button
                      type='button'
                      onClick={() => setPreviewMediaUrl(m.url)}
                      className='overflow-hidden rounded border hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-dark-blue focus:ring-offset-1'
                    >
                      <img
                        src={m.url}
                        alt='Event media'
                        className='block object-cover w-12 h-12'
                      />
                    </button>
                  )}
                  {m.type === 'document' && (
                    <a
                      href={m.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='flex gap-1 items-center px-2 py-1 text-xs text-blue-800 bg-blue-100 rounded border border-blue-200 transition-colors hover:bg-blue-200'
                      title='Open TTQ document'
                    >
                      <FileTextIcon className='flex-shrink-0 w-3 h-3' />
                      TTQ Doc
                    </a>
                  )}
                  {onMediaAccept && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type='button'
                            variant='secondary'
                            size='icon'
                            className='absolute -top-1.5 -right-1.5 h-6 w-6 rounded-full border border-red-200 bg-white shadow-sm text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300'
                            onClick={(e) => {
                              e.stopPropagation()
                              setMediaToRemoveId(m.id)
                            }}
                          >
                            <XIcon className='h-3.5 w-3.5' />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side='top'>
                          <p>Remove media from event</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              ))}
            </div>
          )}

          <Dialog
            open={!!previewMediaUrl}
            onOpenChange={(open) => !open && setPreviewMediaUrl(null)}
          >
            <DialogContent className='max-w-4xl'>
              <DialogHeader>
                <DialogTitle>Media preview</DialogTitle>
              </DialogHeader>
              {previewMediaUrl && (
                <img
                  src={previewMediaUrl}
                  alt='Media preview'
                  className='max-w-full max-h-[70vh] w-auto h-auto object-contain rounded border'
                />
              )}
            </DialogContent>
          </Dialog>

          {isReleased && (
            <div className='p-3 bg-green-50 rounded-md border border-green-200'>
              <div className='flex items-center'>
                <CheckCircleIcon className='mr-2 w-5 h-5 text-green-600' />
                <div>
                  <p className='text-sm font-medium text-green-800'>
                    Event has been released to players
                  </p>
                  {event.released_at && (
                    <p className='text-xs text-green-600'>
                      Released on {new Date(event.released_at).toLocaleString()}
                    </p>
                  )}
                  <p className='mt-1 text-xs text-green-600'>
                    ℹ️ You can still edit this event. Changes will be visible to
                    players immediately.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmationModal
        isOpen={mediaToRemoveId !== null}
        onClose={() => !isRemovingMedia && setMediaToRemoveId(null)}
        onConfirm={async () => {
          if (mediaToRemoveId === null || !roomId) return
          setIsRemovingMedia(true)
          try {
            await deleteEventMediaAPI(roomId, mediaToRemoveId)
            const next = (currentEvent?.media || []).filter(
              (image) => image.id !== mediaToRemoveId
            )
            setFieldValue(`events[${index}].media`, next)
            setMediaToRemoveId(null)
            toast.success('Media removed from event')
          } catch (err) {
            toast.error(
              err instanceof Error ? err.message : 'Failed to remove media'
            )
          } finally {
            setIsRemovingMedia(false)
          }
        }}
        title='Remove media'
        description='Are you sure you want to remove this media from the event? You can add it again later by generating or uploading new media.'
        confirmButtonText={isRemovingMedia ? 'Removing…' : 'Remove'}
        cancelButtonText='Cancel'
      />

      <ConfirmationModal
        isOpen={showReleaseDialog}
        onClose={() => setShowReleaseDialog(false)}
        onConfirm={handleRelease}
        title='Confirm Release'
        description='Are you sure you want to release this event to players? Players will be able to see this event immediately.'
        confirmButtonText='Release'
        cancelButtonText='Cancel'
      />

      <ConfirmationModal
        isOpen={showUnreleaseDialog}
        onClose={() => setShowUnreleaseDialog(false)}
        onConfirm={handleUnrelease}
        title='Confirm Unrelease'
        description='Are you sure you want to unrelease this event? Players will no longer be able to see this event.'
        confirmButtonText='Unrelease'
        cancelButtonText='Cancel'
      />

      <ConfirmationModal
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={() => {
          onDelete(index)
          setShowDeleteDialog(false)
        }}
        title='Confirm Deletion'
        description='Are you sure you want to delete this event? This action cannot be undone.'
        confirmButtonText='Delete'
        cancelButtonText='Cancel'
      />

      {roomId && onMediaAccept && (
        <EventMediaGeneratorChat
          open={showMediaGenerator}
          onOpenChange={setShowMediaGenerator}
          roomId={roomId}
          eventId={event.id}
          scenarioTitle={scenarioTitle}
          eventTitle={currentEvent?.title ?? event.title ?? ''}
          eventText={currentEvent?.text ?? event.text ?? ''}
          existingMedia={currentEvent?.media}
          onAccept={(media) => {
            const current = values.events[index]
            const nextMedia = [...(current.media || []), media]
            setFieldValue(`events[${index}].media`, nextMedia)
            onMediaAccept(index, media)
          }}
        />
      )}
    </>
  )
}
